import React, { useState } from 'react';
import { Clock, Edit, Package, DollarSign, Truck, Calendar, StickyNote, TrendingUp, Zap } from 'lucide-react';
import { useLots } from '../../../../../contexts/LotsContext';
import EditLotModal from '../../modals/EditLotModal';

const InfoTab = ({ lot }) => {
  const { updateLot } = useLots();
  const [notes, setNotes] = useState(lot.observation || '');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Log para depuración
  console.log('InfoTab - lot:', lot);
  console.log('InfoTab - display_id:', lot.display_id);

  // Calcular el promedio de venta
  const averageSalePrice = lot.recentSales.length > 0
    ? lot.recentSales.reduce((sum, sale) => sum + sale.price, 0) / lot.recentSales.length
    : 0;

  const handleNotesSubmit = async () => {
    try {
      await updateLot(lot.id, {
        notes: notes
      });
      setIsEditingNotes(false);
    } catch (error) {
      console.error('Error al actualizar las notas:', error);
    }
  };

  const handleEditModalClose = (changed) => {
    setShowEditModal(false);
    // Si se realizaron cambios y se proporciona una función de recarga
    if (changed) {
      // Podríamos recargar los datos específicos o notificar al usuario
      console.log('Lote actualizado correctamente');
    }
  };

  return (
    <div className="p-5 space-y-5 bg-white dark:bg-gray-800">
      {/* Información principal */}
      <div className="flex justify-between items-start mb-1">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{lot.title}</h2>
          <div className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
            <span className="font-medium">#{lot.display_id}</span>
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-sm flex items-center mt-1">
            <Clock className="w-4 h-4 mr-1" />
            Última actualización {lot.lastUpdate.timestamp}
          </div>
        </div>
        <button 
          onClick={() => setShowEditModal(true)} 
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          <Edit className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Resumen de cantidades */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">Cantidad inicial</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {lot.initialQuantity === null ? (
              <div className="flex items-center gap-1">
                <Zap className="w-6 h-6 text-amber-500" />
                <span className="text-sm text-amber-500">Lote relámpago</span>
              </div>
            ) : (
              <>
                {lot.initialQuantity} {lot.unit}
              </>
            )}
          </div>
        </div>
        <div className="bg-gray-50/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">Cantidad disponible</div>
          <div className={`text-2xl font-bold mt-1 ${
            lot.currentQuantity > 0
              ? 'text-green-600 dark:text-green-400'
              : lot.currentQuantity === 0
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-red-600 dark:text-red-400'
          }`}>
            {lot.currentQuantity} {lot.unit}
          </div>
        </div>
      </div>

      {/* Precios */}
      <div className="bg-gray-50/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-3 shadow-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <span className="font-medium text-gray-900 dark:text-white">Precio sugerido</span>
          </div>
          <div className="text-xl font-bold text-emerald-700 dark:text-emerald-500">
            {lot.minPrice > 0 ? `$${lot.minPrice.toLocaleString()}` : '-'}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <span className="font-medium text-gray-900 dark:text-white">Promedio de venta</span>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {averageSalePrice > 0 ? (
              <span className="text-emerald-700 dark:text-emerald-500">${averageSalePrice.toLocaleString()}</span>
            ) : (
              <span className="text-sm text-gray-500">Sin ventas</span>
            )}
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="space-y-3 py-1">
        {lot.provider && (
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Truck className="w-4 h-4" />
            <span>{lot.provider}</span>
          </div>
        )}
        
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4" />
          <span>Ingreso: {new Date(lot.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Notas */}
      <div className="border dark:border-gray-700 border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <StickyNote className="w-4 h-4" />
            <span className="font-medium">Notas</span>
          </div>
          <button
            onClick={() => setIsEditingNotes(!isEditingNotes)}
            className="text-emerald-700 dark:text-emerald-500 text-sm hover:underline"
          >
            {isEditingNotes ? 'Cancelar' : 'Editar'}
          </button>
        </div>
        
        {isEditingNotes ? (
          <div className="space-y-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-500"
              placeholder="Agregar notas..."
              rows={3}
              maxLength={200}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {notes.length}/200 caracteres
              </span>
              <button
                onClick={handleNotesSubmit}
                className="px-3 py-1 bg-emerald-600 dark:bg-emerald-700 text-white rounded hover:bg-emerald-700 dark:hover:bg-emerald-800"
              >
                Guardar
              </button>
            </div>
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
            {notes || 'Sin notas'}
          </p>
        )}
      </div>

      {/* Modal de edición */}
      {showEditModal && (
        <EditLotModal lot={lot} onClose={handleEditModalClose} />
      )}
    </div>
  );
};

export default InfoTab; 